import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import type { UserDietechFacility, UserPurchasingFacility, UserDepartment } from '../types/user';
import type { TblFacility } from '../types/facility';

// ===== QUERY KEYS =====
export const userFacilityKeys = {
  all: ['userFacility'] as const,
  allFacilities: (facilityId: number, userId: number) =>
    [...userFacilityKeys.all, 'allFacilities', facilityId, userId] as const,
  dietech: (facilityId: number, userId: number) =>
    [...userFacilityKeys.all, 'dietech', facilityId, userId] as const,
  purchasing: (facilityId: number, userId: number) =>
    [...userFacilityKeys.all, 'purchasing', facilityId, userId] as const,
  departments: (facilityId: number, userId: number) =>
    [...userFacilityKeys.all, 'departments', facilityId, userId] as const,
};

// ===== QUERIES =====

/** All facilities the logged-in user has access to (for facility multi-select) */
export function useAllFacilities(facilityId: number, loggedInUserId: number) {
  return useQuery({
    queryKey: userFacilityKeys.allFacilities(facilityId, loggedInUserId),
    queryFn: async () => {
      const { data } = await apiClient.get<TblFacility[]>(
        API.facilityAssociated(facilityId, loggedInUserId),
      );
      return data;
    },
    enabled: facilityId > 0 && loggedInUserId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserDietechFacilities(facilityId: number, userId: number) {
  return useQuery({
    queryKey: userFacilityKeys.dietech(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<UserDietechFacility[]>(
        API.userFacilityDietech(facilityId, userId),
      );
      return data;
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useUserPurchasingFacilities(facilityId: number, userId: number) {
  return useQuery({
    queryKey: userFacilityKeys.purchasing(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<UserPurchasingFacility[]>(
        API.userFacilityPurchasing(facilityId, userId),
      );
      return data;
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useUserDepartments(facilityId: number, userId: number) {
  return useQuery({
    queryKey: userFacilityKeys.departments(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<UserDepartment[]>(
        API.userFacilityDepartments(facilityId, userId),
      );
      return data;
    },
    enabled: facilityId > 0 && userId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// ===== MUTATIONS =====

/**
 * The three save endpoints replace the user's whole list, and the API refuses an empty list
 * unless the request says `?clearAll=true` (see replaceAllPlan in pages/users/userMappingSave).
 */
const clearAllParams = (clearAll?: boolean) => (clearAll ? { params: { clearAll: true } } : undefined);

export function useSaveDietechFacilities(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, facilityIds, clearAll }: { userId: number; facilityIds: number[]; clearAll?: boolean }) => {
      await apiClient.post(API.userFacilitySaveDietech(facilityId, userId), facilityIds, clearAllParams(clearAll));
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: userFacilityKeys.dietech(facilityId, variables.userId),
      });
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to save Dietech facilities', 'error');
    },
  });
}

export function useSavePurchasingFacilities(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, facilityIds, clearAll }: { userId: number; facilityIds: number[]; clearAll?: boolean }) => {
      await apiClient.post(API.userFacilitySavePurchasing(facilityId, userId), facilityIds, clearAllParams(clearAll));
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: userFacilityKeys.purchasing(facilityId, variables.userId),
      });
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to save Purchasing facilities', 'error');
    },
  });
}

export function useSaveDepartments(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, departments, clearAll }: { userId: number; departments: UserDepartment[]; clearAll?: boolean }) => {
      await apiClient.post(API.userFacilitySaveDepartments(facilityId, userId), departments, clearAllParams(clearAll));
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: userFacilityKeys.departments(facilityId, variables.userId),
      });
    },
    onError: () => {
      useNotificationStore.getState().addNotification('Failed to save departments', 'error');
    },
  });
}
